import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { setToolbarIcon } from '../engine/viewer-icons';

/** A PDF.js explicit destination: the page, the way to fit it, and the coordinates that go with that. */
export type Destination = NonNullable<Awaited<ReturnType<PDFDocumentProxy['getDestination']>>>;

type OutlineItem = Awaited<ReturnType<PDFDocumentProxy['getOutline']>>[number];

/** Where the reader is: the page on screen, and how far down it, in unrotated viewport units. */
export interface OutlinePosition {
  page: number;
  top: number;
}

export interface PdfOutlineOptions {
  pdf: PDFDocumentProxy;
  document: Document;
  controls: HTMLElement;
  workspace: HTMLElement;
  viewport: HTMLElement;
  navigate: (destination: Destination, current: () => boolean) => Promise<void>;
  onLayout: () => void;
  isActive: () => boolean;
  getPosition?: () => OutlinePosition | null;
  onVisibleChange?: (visible: boolean) => void;
}

export interface PdfOutline {
  setVisible(value: boolean): void;
  sync(force?: boolean): void;
  resize(): void;
  destroy(): void;
}

/** A bookmark with children: the control that folds it away, and the branch it hangs from. */
interface OutlineBranch {
  expand: HTMLButtonElement;
  children: HTMLUListElement;
  label: string;
  parent: OutlineBranch | null;
}

/** A bookmark that leads somewhere, before its page is known. */
interface OutlineSection {
  dest: string | Destination;
  link: HTMLElement;
  pageBadge: HTMLSpanElement;
  parent: OutlineBranch | null;
  order: number;
}

/** The same bookmark once PDF.js has said which page it lands on and where. */
interface OutlineLocation extends OutlineSection {
  page: number;
  top: number;
}

interface QueuedLevel {
  items: Array<OutlineItem>;
  list: HTMLUListElement;
  parent: OutlineBranch | null;
}

/** A bookmark's control, and the badge that will carry its page once that page is known. */
interface OutlineLink {
  link: HTMLAnchorElement | HTMLButtonElement;
  pageBadge?: HTMLSpanElement;
}

/** What building the tree needs from the panel around it. */
interface TreeContext {
  document: Document;
  message: HTMLElement;
  sections: Array<OutlineSection>;
  linkFor: (item: OutlineItem, label: string) => OutlineLink;
}

const MAX_BOOKMARKS = 10000;
const INDEXING_READERS = 4;
const NARROW_VIEWPORT = 700;
const DEFAULT_WIDTH = 260;
const KEYBOARD_STEP = 16;
const PART_ATTRIBUTE = 'part';
const LABEL_ATTRIBUTE = 'aria-label';
const EXPANDED_ATTRIBUTE = 'aria-expanded';
const WIDTH_PROPERTY = '--pdf-outline-width';
const CURRENT_ATTRIBUTE = 'aria-current';
const MINIMUM_ATTRIBUTE = 'aria-valuemin';
const MAXIMUM_ATTRIBUTE = 'aria-valuemax';

/**
 * The page a destination points at.
 *
 * @param {PDFDocumentProxy} pdf The open document.
 * @param {Destination} destination The destination to resolve.
 * @return {Promise<number|null>} Zero-based page index, or null when it falls outside the document.
 */
async function pageIndexOf(pdf: PDFDocumentProxy, destination: Destination): Promise<number | null> {
  const index = Number.isInteger(destination[0]) ? destination[0] : await pdf.getPageIndex(destination[0]);

  return Number.isInteger(index) && index >= 0 && index < pdf.numPages ? index : null;
}

/**
 * How far down its page a destination lands. A destination that only names a page, or that is written
 * in a form carrying no vertical position, starts at the top of it.
 *
 * @param {PDFPageProxy} page The page the destination lands on.
 * @param {Destination} destination The destination to place.
 * @return {number} Distance from the top of the page, in unrotated viewport units.
 */
function destinationTop(page: PDFPageProxy, destination: Destination): number {
  const viewport = page.getViewport({ scale: 1 });
  const mode = destination[1]?.name;

  if (mode === 'XYZ' || mode === 'FitH' || mode === 'FitBH') {
    const y = mode === 'XYZ' ? destination[3] : destination[2];
    const x = mode === 'XYZ' && Number.isFinite(destination[2]) ? destination[2] : page.view[0];

    return Number.isFinite(y) ? viewport.convertToViewportPoint(x, y)[1] : 0;
  }

  // Each corner in turn rather than convertToViewportRectangle: PDF.js no longer has that method, and
  // this is what it did — the same transform applied to both corners.
  if (mode === 'FitR' && destination.slice(2, 6).every(Number.isFinite)) {
    const [, first] = viewport.convertToViewportPoint(destination[2], destination[3]);
    const [, second] = viewport.convertToViewportPoint(destination[4], destination[5]);

    return Math.min(first, second);
  }

  return 0;
}

/** What the handle between the panel and the pages needs in order to move it. */
interface SplitterOptions {
  document: Document;
  panel: HTMLElement;
  workspace: HTMLElement;
  viewport: HTMLElement;
  onLayout: () => void;
  isActive: () => boolean;
  onEscape: () => void;
}

interface OutlineSplitter {
  element: HTMLElement;
  resize(): void;
  stop(): void;
  destroy(): void;
}

/**
 * The handle that sets how wide the contents panel is, by pointer or by arrow key. Below a narrow
 * viewport the panel lies over the pages instead of beside them, and the width it may take changes
 * with it, so the width the reader chose is remembered and applied within whatever the room allows.
 *
 * @param {SplitterOptions} options Where to build, what to move, and whom to tell that it moved.
 * @return {OutlineSplitter} The handle, and the ways the panel drives it.
 */
function createOutlineSplitter(options: SplitterOptions): OutlineSplitter {
  const { document, panel, workspace, viewport, onLayout, isActive, onEscape } = options;
  const splitter = document.createElement('div');

  splitter.className = 'pdf-outline-splitter';
  splitter.setAttribute(PART_ATTRIBUTE, 'viewer-contents-splitter');
  splitter.tabIndex = 0;
  splitter.hidden = true;
  splitter.setAttribute('role', 'separator');
  splitter.setAttribute(LABEL_ATTRIBUTE, 'Resize PDF contents');
  splitter.setAttribute('aria-orientation', 'vertical');
  splitter.setAttribute('aria-controls', panel.id);

  let preferredWidth = DEFAULT_WIDTH;
  let drag: { id: number; x: number; width: number } | null = null;
  let layoutFrame = 0;

  function resize(): void {
    const overlay = viewport.clientWidth < NARROW_VIEWPORT;

    workspace.classList.toggle('outline-overlay', overlay);

    const maximum = Math.max(80, Math.min(800, overlay ? viewport.clientWidth * 0.85 : viewport.clientWidth - 248));
    const minimum = Math.min(160, maximum);
    const width = Math.round(Math.max(minimum, Math.min(maximum, preferredWidth)));

    workspace.style.setProperty(WIDTH_PROPERTY, `${width}px`);
    splitter.setAttribute(MINIMUM_ATTRIBUTE, String(Math.round(minimum)));
    splitter.setAttribute(MAXIMUM_ATTRIBUTE, String(Math.round(maximum)));
    splitter.setAttribute('aria-valuenow', String(width));
  }

  function setWidth(width: number): void {
    preferredWidth = Math.max(
      Number(splitter.getAttribute(MINIMUM_ATTRIBUTE)),
      Math.min(Number(splitter.getAttribute(MAXIMUM_ATTRIBUTE)), width)
    );
    resize();
    cancelAnimationFrame(layoutFrame);
    layoutFrame = requestAnimationFrame(() => {
      if (isActive()) onLayout();
    });
  }

  function stop(): void {
    const previous = drag;

    drag = null;
    workspace.classList.remove('resizing-outline');

    if (previous && splitter.hasPointerCapture(previous.id)) splitter.releasePointerCapture(previous.id);
  }

  splitter.onpointerdown = (event: PointerEvent): void => {
    if (event.button !== 0) return;

    event.preventDefault();
    splitter.focus({ preventScroll: true });
    drag = { id: event.pointerId, x: event.clientX, width: panel.getBoundingClientRect().width };
    splitter.setPointerCapture(event.pointerId);
    workspace.classList.add('resizing-outline');
  };
  splitter.onpointermove = (event: PointerEvent): void => {
    if (drag?.id === event.pointerId) setWidth(drag.width + event.clientX - drag.x);
  };
  splitter.onpointerup = stop;
  splitter.onpointercancel = stop;
  splitter.onlostpointercapture = stop;
  splitter.onkeydown = (event: KeyboardEvent): void => {
    const width = Number(splitter.getAttribute('aria-valuenow'));
    const widths: Record<string, number> = {
      ArrowLeft: width - KEYBOARD_STEP,
      ArrowRight: width + KEYBOARD_STEP,
      Home: Number(splitter.getAttribute(MINIMUM_ATTRIBUTE)),
      End: Number(splitter.getAttribute(MAXIMUM_ATTRIBUTE))
    };

    if (Object.hasOwn(widths, event.key)) {
      event.preventDefault();
      setWidth(widths[event.key]);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
    }
  };

  resize();

  return {
    element: splitter,
    resize,
    stop,
    destroy(): void {
      stop();
      cancelAnimationFrame(layoutFrame);
      workspace.classList.remove('outline-overlay');
      workspace.style.removeProperty(WIDTH_PROPERTY);
      splitter.remove();
    }
  };
}

/**
 * Fills the panel with the bookmark tree. Built iteratively so that a deeply nested outline does not
 * overflow the call stack.
 *
 * @param {Array<OutlineItem>} outline The bookmarks PDF.js found.
 * @param {HTMLUListElement} list The list to fill.
 * @param {TreeContext} context Where to build, where to report, and how to make each bookmark's control.
 */
function buildOutlineTree(outline: Array<OutlineItem>, list: HTMLUListElement, context: TreeContext): void {
  const { document, message, sections, linkFor } = context;
  const queue: Array<QueuedLevel> = [{ items: outline, list, parent: null }];
  let count = 0;

  while (queue.length) {
    const current = queue.pop() as QueuedLevel;

    for (const item of current.items) {
      if (++count > MAX_BOOKMARKS) {
        message.textContent = 'Showing the first 10,000 bookmarks.';
        break;
      }

      const li = document.createElement('li');
      const row = document.createElement('div');

      row.className = 'outline-row';

      const label = item.title || 'Untitled section';

      if (item.items?.length) {
        const expand = document.createElement('button');

        expand.type = 'button';
        expand.className = 'outline-expand';
        expand.setAttribute(PART_ATTRIBUTE, 'viewer-contents-expand');
        expand.setAttribute(LABEL_ATTRIBUTE, `Expand ${label}`);
        expand.setAttribute(EXPANDED_ATTRIBUTE, 'false');

        const children = document.createElement('ul');

        children.hidden = true;
        expand.onclick = (): void => {
          children.hidden = !children.hidden;
          expand.setAttribute(EXPANDED_ATTRIBUTE, String(!children.hidden));
          expand.setAttribute(LABEL_ATTRIBUTE, `${children.hidden ? 'Expand' : 'Collapse'} ${label}`);
        };
        row.append(expand);
        li.append(row, children);
        queue.push({ items: item.items, list: children, parent: { expand, children, label, parent: current.parent } });
      } else {
        row.classList.add('outline-leaf');
        li.append(row);
      }

      const { link, pageBadge } = linkFor(item, label);

      if (pageBadge) {
        sections.push({ dest: item.dest as string | Destination, link, pageBadge, parent: current.parent, order: count });
      }

      link.className = 'outline-link';
      link.setAttribute(PART_ATTRIBUTE, 'viewer-contents-link');
      link.textContent = label;

      if (link.tagName === 'A') {
        const external = document.createElement('span');

        external.textContent = ' ↗';
        external.setAttribute('aria-hidden', 'true');
        link.append(external);
      }

      if (item.bold) link.style.fontWeight = 'bold';

      if (item.italic) link.style.fontStyle = 'italic';

      row.append(link);

      if (pageBadge) row.append(pageBadge);

      current.list.append(li);
    }

    if (count > MAX_BOOKMARKS) break;
  }
}

/**
 * The contents panel: the bookmarks PDF.js found, as a tree the reader can fold, resize, and read
 * along with. Labels are set as text and link targets are limited to web and mail addresses, so a
 * document cannot have the viewer open anything on its behalf.
 *
 * @param {PdfOutlineOptions} options The document, where to build, and how to move and measure.
 * @return {Promise<PdfOutline|null>} Handles to drive the panel, or null when there is nothing to show.
 */
export async function createPDFOutline(options: PdfOutlineOptions): Promise<PdfOutline | null> {
  const { pdf, document, controls, workspace, viewport, navigate, onLayout, isActive } = options;
  const { getPosition, onVisibleChange } = options;
  const outline = await pdf.getOutline();

  if (!isActive() || !outline?.length) return null;

  const toggle = document.createElement('button');

  toggle.type = 'button';
  toggle.setAttribute(LABEL_ATTRIBUTE, 'Contents');
  toggle.title = 'Contents';
  setToolbarIcon(toggle, 'contents');
  toggle.className = 'contents-toggle';
  toggle.setAttribute(PART_ATTRIBUTE, 'viewer-contents-toggle');
  toggle.setAttribute(EXPANDED_ATTRIBUTE, 'false');

  const panel = document.createElement('nav');

  panel.className = 'pdf-outline';
  panel.setAttribute(PART_ATTRIBUTE, 'viewer-contents');
  panel.id = 'pdf-contents';
  toggle.setAttribute('aria-controls', panel.id);
  panel.hidden = true;
  panel.setAttribute(LABEL_ATTRIBUTE, 'PDF contents');

  const splitter = createOutlineSplitter({
    document,
    panel,
    workspace,
    viewport,
    onLayout,
    isActive,
    onEscape: (): void => setOpen(false)
  });

  const heading = document.createElement('div');

  heading.className = 'outline-heading';
  heading.setAttribute(PART_ATTRIBUTE, 'viewer-contents-heading');

  const title = document.createElement('strong');

  title.textContent = 'Contents';

  const close = document.createElement('button');

  close.type = 'button';
  close.className = 'outline-close';
  close.setAttribute(PART_ATTRIBUTE, 'viewer-contents-close');
  close.title = 'Close contents';
  setToolbarIcon(close, 'close');
  close.setAttribute(LABEL_ATTRIBUTE, 'Close contents');
  heading.append(title, close);

  const message = document.createElement('div');

  message.className = 'outline-message';
  message.setAttribute('role', 'status');
  panel.append(heading, message);

  const sections: Array<OutlineSection> = [];
  const destinations = new Map<string | Destination, Promise<Destination | null>>();
  const locations: Array<OutlineLocation> = [];
  let selected: HTMLElement | undefined;
  let navigationId = 0;
  let indexed = false;
  let indexing: Promise<void> | undefined;
  let navigating = false;

  function destinationFor(value: string | Destination): Promise<Destination | null> {
    let pending = destinations.get(value);

    if (!pending) {
      pending = typeof value === 'string' ? pdf.getDestination(value) : Promise.resolve(value);
      destinations.set(value, pending);
    }

    return pending;
  }

  function reveal(section: OutlineSection): void {
    for (let parent = section.parent; parent; parent = parent.parent) {
      parent.children.hidden = false;
      parent.expand.setAttribute(EXPANDED_ATTRIBUTE, 'true');
      parent.expand.setAttribute(LABEL_ATTRIBUTE, `Collapse ${parent.label}`);
    }

    const link = section.link.getBoundingClientRect();
    const bounds = panel.getBoundingClientRect();

    if (link.top < bounds.top + heading.offsetHeight || link.bottom > bounds.bottom) {
      panel.scrollTop += link.top - bounds.top - heading.offsetHeight - 12;
    }
  }

  function sync(force = false): void {
    if (!isActive() || !indexed || panel.hidden || navigating || !getPosition) return;

    const position = getPosition();

    if (!position) return;

    let low = 0;
    let high = locations.length;

    while (low < high) {
      const middle = (low + high) >> 1;
      const item = locations[middle];

      if (item.page < position.page || (item.page === position.page && item.top <= position.top + 2)) low = middle + 1;
      else high = middle;
    }

    const section = locations[low - 1];

    if (selected !== section?.link) {
      selected?.removeAttribute(CURRENT_ATTRIBUTE);
      selected = section?.link;
      selected?.setAttribute(CURRENT_ATTRIBUTE, 'location');

      if (section) reveal(section);
    } else if (force && section) reveal(section);
  }

  function showPage(section: OutlineSection, index: number, labels: Array<string> | null): void {
    const number = index + 1;
    const label = labels?.[index] || String(number);

    section.pageBadge.textContent = label;
    section.pageBadge.title = label === String(number) ? `Page ${number}` : `Page ${label} (PDF page ${number})`;
    section.pageBadge.setAttribute(LABEL_ATTRIBUTE, section.pageBadge.title);
    section.pageBadge.hidden = false;
  }

  function indexSections(): Promise<void> {
    if (indexing) return indexing;

    const pageLabels = pdf.getPageLabels().catch(() => null);
    let next = 0;

    indexing = Promise.all(
      Array.from({ length: Math.min(INDEXING_READERS, sections.length) }, async () => {
        const labels = await pageLabels;

        while (isActive() && next < sections.length) {
          const section = sections[next++];

          try {
            const destination = await destinationFor(section.dest);

            if (!isActive() || !Array.isArray(destination)) continue;

            const index = await pageIndexOf(pdf, destination);

            if (!isActive() || index === null) continue;

            showPage(section, index, labels);

            const page = await pdf.getPage(index + 1);

            if (!isActive()) return;

            locations.push({ ...section, page: index + 1, top: Math.max(0, destinationTop(page, destination)) });
          } catch {
            /* A broken bookmark does not disable other sections. */
          }
        }
      })
    ).then(() => {
      indexed = true;
      locations.sort((a, b) => a.page - b.page || a.top - b.top || a.order - b.order);
      sync(true);
    });

    return indexing;
  }

  const setOpen = (value: boolean, focus = true): void => {
    const open = Boolean(value);

    if (open === !panel.hidden) return;

    splitter.stop();
    panel.hidden = !open;
    splitter.element.hidden = !open;
    splitter.resize();
    toggle.setAttribute(EXPANDED_ATTRIBUTE, String(open));
    onLayout();

    if (open) indexSections().then(() => sync(true));

    if (focus) (open ? close : toggle).focus({ preventScroll: true });

    onVisibleChange?.(open);
  };

  toggle.onclick = (): void => setOpen(panel.hidden);
  close.onclick = (): void => setOpen(false);
  panel.onkeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  function createDestinationLink(item: OutlineItem): HTMLButtonElement {
    const link = document.createElement('button');

    link.type = 'button';
    link.onclick = async (): Promise<void> => {
      const id = ++navigationId;

      message.textContent = '';
      navigating = true;

      try {
        const destination = await destinationFor(item.dest as string | Destination);

        if (!isActive() || id !== navigationId) return;

        if (!Array.isArray(destination)) throw Error('This bookmark has no valid destination.');

        await navigate(destination, () => isActive() && id === navigationId);

        if (!isActive() || id !== navigationId) return;

        selected?.removeAttribute(CURRENT_ATTRIBUTE);
        selected = link;
        link.setAttribute(CURRENT_ATTRIBUTE, 'location');

        if (viewport.clientWidth < NARROW_VIEWPORT) setOpen(false, false);

        viewport.focus({ preventScroll: true });
      } catch {
        if (isActive() && id === navigationId) message.textContent = 'Unable to open this bookmark.';
      } finally {
        if (id === navigationId) {
          navigating = false;
          sync();
        }
      }
    };

    return link;
  }

  // Only addresses the browser opens by itself: a document must not be able to point the viewer at a
  // scheme that hands the bookmark to something else on the reader's machine.
  function createUrlLink(item: OutlineItem, label: string): HTMLAnchorElement | HTMLButtonElement {
    let address = '';

    try {
      const parsed = new URL(item.url as string);

      if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) address = parsed.href;
    } catch {
      /* Unsupported actions remain disabled. */
    }

    if (!address) {
      const disabled = document.createElement('button');

      disabled.type = 'button';
      disabled.disabled = true;
      disabled.title = 'This bookmark action is not supported.';

      return disabled;
    }

    const link = document.createElement('a');

    link.href = address;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute(LABEL_ATTRIBUTE, `${label} (external link)`);

    return link;
  }

  function linkFor(item: OutlineItem, label: string): OutlineLink {
    if (!item.dest) return { link: createUrlLink(item, label) };

    const pageBadge = document.createElement('span');

    pageBadge.className = 'outline-page';
    pageBadge.hidden = true;

    return { link: createDestinationLink(item), pageBadge };
  }

  const list = document.createElement('ul');

  panel.append(list);
  buildOutlineTree(outline, list, { document, message, sections, linkFor });
  controls.prepend(toggle);
  workspace.prepend(panel, splitter.element);

  return {
    setVisible: (value: boolean): void => setOpen(value, false),
    sync,
    resize: splitter.resize,
    destroy(): void {
      navigationId++;
      splitter.destroy();
      toggle.remove();
      panel.remove();
    }
  };
}
